import { RadialBarChart, RadialBar, Legend, ResponsiveContainer, Tooltip } from 'recharts'

interface RadialBarComponentProps {
  data: Array<Record<string, any>>
  nameKey: string
  valueKey: string
  title?: string
  maxValue?: number
  colors?: string[]
  loading?: boolean
}

// Paleta de colores por defecto
const DEFAULT_COLORS = [
  '#10b981', // Verde
  '#3b82f6', // Azul
  '#f59e0b', // Naranja
  '#ef4444', // Rojo
  '#8b5cf6', // Morado
  '#14b8a6', // Teal
  '#ec4899', // Rosa
  '#f97316', // Naranja oscuro
]

export function RadialBarComponent({
  data,
  nameKey,
  valueKey,
  title,
  maxValue,
  colors = DEFAULT_COLORS,
  loading = false,
}: RadialBarComponentProps) {
  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-6">
        {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
        <div className="h-[400px] flex items-center justify-center text-gray-500">
          No hay datos disponibles
        </div>
      </div>
    )
  }

  // Transformar datos para radial bar chart
  const radialData = data.map((item, index) => ({
    name: String(item[nameKey] || 'Sin nombre'),
    value: Number(item[valueKey]) || 0,
    fill: colors[index % colors.length],
  }))

  // Determinar max si no se especifica
  const values = radialData.map((d) => d.value)
  const calculatedMax = maxValue || Math.max(...values) * 1.1

  // Custom label para mostrar porcentajes
  const renderCustomLabel = (props: any) => {
    const { cx, cy, midAngle, outerRadius, fill, value, name } = props
    const RADIAN = Math.PI / 180
    const radius = outerRadius + 25
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    const percentage = ((value / calculatedMax) * 100).toFixed(0)

    return (
      <text
        x={x}
        y={y}
        fill={fill}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
        fontWeight="bold"
      >
        {`${name}: ${percentage}%`}
      </text>
    )
  }

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      const percentage = ((data.value / calculatedMax) * 100).toFixed(1)

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900 mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">
            Valor: <span className="font-medium">{data.value.toLocaleString('es-ES')}</span>
          </p>
          <p className="text-sm text-gray-600">
            Progreso: <span className="font-medium">{percentage}%</span>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white border rounded-lg p-6">
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}

      <ResponsiveContainer width="100%" height={400}>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius="10%"
          outerRadius="90%"
          data={radialData}
          startAngle={180}
          endAngle={-180}
        >
          <RadialBar
            label={renderCustomLabel}
            background
            dataKey="value"
          />
          <Legend
            iconSize={10}
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={{ fontSize: '12px' }}
            formatter={(value, entry: any) => {
              const percentage = ((entry.payload.value / calculatedMax) * 100).toFixed(1)
              return `${value} (${percentage}%)`
            }}
          />
          <Tooltip content={<CustomTooltip />} />
        </RadialBarChart>
      </ResponsiveContainer>

      {/* Info adicional */}
      <div className="mt-4 pt-4 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-600">
          Rango máximo: <span className="font-semibold text-gray-900">{calculatedMax.toLocaleString('es-ES')}</span>
        </p>
      </div>
    </div>
  )
}
